/**
 * Dialog zum Bearbeiten (PATCH) oder Neu-Anlegen (POST) eines Urlaubs für einen
 * bestimmten Mitarbeiter – inklusive rückwirkender Zeiträume. Zeigt eine Live-
 * Vorschau der Werktage.
 */
import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, CalendarClock } from 'lucide-react';
import { vacationsAPI } from '../../lib/api';
import { toast } from 'sonner';

const VACATION_TYPES = ['Urlaub', 'Sonderurlaub', 'Unbezahlter Urlaub'];

export function VacationEditorDialog({ open, onOpenChange, mode = 'edit', vacation, employeeId, employeeName, onSaved }) {
  const [range, setRange] = useState({ from: null, to: null });
  const [vacationType, setVacationType] = useState('Urlaub');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && vacation) {
      setRange({ from: parseISO(vacation.start_date), to: parseISO(vacation.end_date) });
      setVacationType(vacation.vacation_type || 'Urlaub');
      setNotes(vacation.notes || '');
    } else {
      setRange({ from: null, to: null });
      setVacationType('Urlaub');
      setNotes('');
    }
    setPreview(null);
  }, [open, mode, vacation]);

  useEffect(() => {
    if (!range.from || !range.to) { setPreview(null); return; }
    const empId = mode === 'edit' ? vacation?.employee_id : employeeId;
    vacationsAPI
      .preview(format(range.from, 'yyyy-MM-dd'), format(range.to, 'yyyy-MM-dd'), empId || null)
      .then((res) => setPreview(res.data))
      .catch(() => setPreview(null));
  }, [range.from, range.to, mode, vacation, employeeId]);

  const handleSave = async () => {
    if (!range.from || !range.to) {
      toast.error('Bitte Start- und Enddatum wählen');
      return;
    }
    setSaving(true);
    const payload = {
      start_date: format(range.from, 'yyyy-MM-dd'),
      end_date: format(range.to, 'yyyy-MM-dd'),
      vacation_type: vacationType,
      notes,
    };
    try {
      if (mode === 'edit') {
        await vacationsAPI.update(vacation.id, payload);
        toast.success('Urlaub angepasst');
      } else {
        await vacationsAPI.create({ ...payload, employee_id: employeeId });
        toast.success('Urlaub eingetragen');
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const now = new Date();
  const maxYear = now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="vacation-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" />
            {mode === 'edit' ? 'Urlaub bearbeiten' : 'Urlaub eintragen'}
          </DialogTitle>
          <DialogDescription>
            {employeeName ? `Für ${employeeName}. ` : ''}Zeitraum wählen – rückwirkende Daten sind erlaubt.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center overflow-x-auto">
          <Calendar
            mode="range"
            selected={range}
            onSelect={(r) => setRange(r || { from: null, to: null })}
            locale={de}
            className="rounded-md border"
            data-testid="vacation-editor-calendar"
            fromDate={new Date(now.getFullYear() - 1, 0, 1)}
            toDate={new Date(maxYear, 11, 31)}
          />
        </div>

        {range.from && range.to && (
          <div className="rounded-lg bg-secondary/40 p-3 text-sm" data-testid="vacation-editor-preview">
            {format(range.from, 'dd.MM.yyyy', { locale: de })} – {format(range.to, 'dd.MM.yyyy', { locale: de })}
            {preview != null && (
              <span className="font-medium"> · {preview.business_days} Werktag{preview.business_days === 1 ? '' : 'e'}</span>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          <div className="space-y-1.5">
            <Label>Art</Label>
            <Select value={vacationType} onValueChange={setVacationType}>
              <SelectTrigger data-testid="vacation-editor-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VACATION_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Notiz (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="z. B. Grund / Hinweis" data-testid="vacation-editor-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={saving} data-testid="vacation-editor-cancel">
            Abbrechen
          </Button>
          <Button className="rounded-full" onClick={handleSave} disabled={saving || !range.from || !range.to} data-testid="vacation-editor-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default VacationEditorDialog;
